import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, MapPin, Search } from "lucide-react";
import { inr } from "@/lib/format";
import { useApi } from "@/lib/useApi";
import { Card, EmptyState, ErrorState, ListSkeleton } from "@/components/common";
import { Chips } from "@/components/form";
import { photoSrc } from "@/components/Photos";
import { ROOM_AMENITY } from "@/components/RoomAmenities";
import { Slider } from "@/components/ui/slider";

const STEP = 500;

function PriceFilter({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const range = useApi<{ min: number; max: number }>("/listings/rent-range");
  const top = Math.ceil((range.data?.max || 0) / STEP) * STEP;
  const bottom = Math.min(top, Math.max(STEP, Math.floor((range.data?.min || 0) / STEP) * STEP));
  const [draft, setDraft] = useState(value || top);
  useEffect(() => { setDraft(value || top); }, [value, top]);
  if (!top || top <= bottom) return null;
  return (
    <div data-testid="find-price">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500">Max rent per bed</p>
        <p className="text-sm font-bold font-mono-num text-[#131e2e]" data-testid="find-price-value">{draft < top ? `Up to ${inr(draft)}` : "Any budget"}</p>
      </div>
      <Slider data-testid="find-price-slider" min={bottom} max={top} step={STEP} value={[draft]}
        onValueChange={(v) => setDraft(v[0])} onValueCommit={(v) => onChange(v[0] >= top ? 0 : v[0])}
        className="py-2 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-[#131e2e] [&_[role=slider]]:bg-white [&>span:first-child]:bg-[#dbe7f5] [&>span:first-child>span]:bg-[#131e2e]" />
      <div className="flex justify-between text-[11px] text-slate-400 font-mono-num mt-1"><span>{inr(bottom)}</span><span>{inr(top)}</span></div>
    </div>
  );
}

function FeatureFilter({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (k: string) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div data-testid="find-features">
      <p className="text-xs font-semibold text-slate-500 mb-1.5">Room features</p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {Object.entries(ROOM_AMENITY).map(([k, label]) => (
          <button key={k} type="button" data-testid={`find-feature-${k}`} onClick={() => toggle(k)}
            className={`shrink-0 h-9 px-3.5 rounded-full border text-xs font-semibold pg-tap ${value.includes(k) ? "bg-[#3d52a0] border-[#3d52a0] text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{label}</button>
        ))}
        {value.length > 0 && <button type="button" data-testid="find-features-clear" onClick={() => onChange([])} className="shrink-0 h-9 px-3 text-xs font-semibold text-slate-500 underline">Clear</button>}
      </div>
    </div>
  );
}

export const GENDER: Record<string, string> = { boys: "Boys", girls: "Girls", coed: "Co-living" };
export const AMENITY: Record<string, string> = {
  wifi: "WiFi", food: "Food", ac: "AC", laundry: "Laundry", housekeeping: "Housekeeping", parking: "Parking",
  power_backup: "Power backup", hot_water: "Hot water", cctv: "CCTV", gym: "Gym",
};

function ListingCard({ l }: { l: any }) {
  return (
    <Link to={`/resident/pg/${l.id}`} data-testid={`listing-card-${l.id}`} className="block">
      <Card className="p-0 overflow-hidden pg-tap hover:border-[#a9c4e6]">
        <div className="aspect-[16/9] bg-[#e8f1fb] relative">
          {l.photos?.[0] ? <img src={photoSrc(l.photos[0].url)} alt={l.name} className="h-full w-full object-cover" loading="lazy" data-testid={`listing-cover-${l.id}`} /> : <div className="h-full w-full grid place-items-center text-[#131e2e]/40"><BedDouble className="h-10 w-10" /></div>}
          {l.photos?.length > 1 && <span className="absolute bottom-2 right-2 text-[11px] font-semibold bg-black/55 text-white rounded-full px-2 py-0.5">{l.photos.length} photos</span>}
        </div>
        <div className="p-4">
        <div className="flex justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-lg truncate">{l.name}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{[l.locality, l.city].filter(Boolean).join(", ")}</p>
          </div>
          <span className="h-fit text-[11px] font-bold rounded-full bg-[#e8f1fb] text-[#131e2e] px-2.5 py-1">{GENDER[l.gender]}</span>
        </div>
        <p className="mt-3 text-xl font-extrabold font-mono-num text-[#131e2e]">{inr(l.rent_min)}{l.rent_max > l.rent_min ? ` – ${inr(l.rent_max)}` : ""}<span className="text-xs font-semibold text-slate-500"> /bed/mo</span></p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className={`text-xs font-semibold rounded-full px-2.5 py-1 flex items-center gap-1 ${l.vacant_beds ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}><BedDouble className="h-3 w-3" />{l.vacant_beds ? `${l.vacant_beds} beds free` : "Full"}</span>
          {l.matching_rooms > 0 && <span data-testid={`listing-match-${l.id}`} className="text-xs font-semibold rounded-full px-2.5 py-1 bg-[#e8f1fb] text-[#131e2e]">{l.matching_rooms} matching room{l.matching_rooms > 1 ? "s" : ""}</span>}
          {l.amenities.slice(0, 4).map((a) => <span key={a} className="text-xs rounded-full bg-slate-100 text-slate-600 px-2.5 py-1">{AMENITY[a]}</span>)}
        </div>
        </div>
      </Card>
    </Link>
  );
}

export default function FindPG() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("");
  const [feats, setFeats] = useState<string[]>([]);
  const [maxRent, setMaxRent] = useState(0);
  const cities = useApi<string[]>("/listings/cities");
  const { data, loading, error, reload } = useApi<any[]>(`/listings?q=${encodeURIComponent(q)}&city=${encodeURIComponent(city)}&gender=${gender}&features=${feats.join(",")}&max_rent=${maxRent}`);
  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Find a PG</h1>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input data-testid="find-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by PG name or area"
          className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-3 outline-none focus:ring-2 focus:ring-[#131e2e]" />
      </div>
      <div className="space-y-2 mb-4 overflow-x-auto">
        <Chips testId="find-city" value={city} onChange={setCity} options={[{ value: "", label: "All cities" }, ...(cities.data || []).map((c) => ({ value: c, label: c }))]} />
        <Chips testId="find-gender" value={gender} onChange={setGender} options={[{ value: "", label: "Any" }, { value: "boys", label: "Boys" }, { value: "girls", label: "Girls" }, { value: "coed", label: "Co-living" }]} />
        <FeatureFilter value={feats} onChange={setFeats} />
        <PriceFilter value={maxRent} onChange={setMaxRent} />
      </div>
      {loading && !data ? <ListSkeleton /> : error ? <ErrorState message={error} onRetry={reload} /> : !data.length ? (
        <EmptyState testId="find-empty" icon={Search} title="No PGs found" text={feats.length || maxRent ? "No PG has a free room matching these filters. Try a higher budget or fewer features." : "Try another area or clear filters."} />
      ) : <div className="space-y-3 pg-stagger">{data.map((l) => <ListingCard key={l.id} l={l} />)}</div>}
    </div>
  );
}
